import { useMemo, useRef, useState, type ReactElement } from 'react';
import { useController, type Control } from 'react-hook-form';
import { Clock, Plus, Search, Trash2, Check, Info } from 'lucide-react';
import {
  CURATED_PII_INFO_TYPES,
  getCuratedPiiInfoType,
  isSensitivePiiInfoType,
  type PiiInfoTypeOption,
} from '@kirin/api-client/contracts/pii-info-types';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Popover,
  PopoverContent,
  PopoverAnchor,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { usePiiInfoTypes } from '@/hooks/api/usePiiInfoTypes';
import type { PolicySettingsFormData } from '../types';

interface PiiTypesFieldProps {
  control: Control<PolicySettingsFormData>;
  readOnly?: boolean;
}

interface PiiRow {
  name: string;
  label: string;
  sensitive: boolean;
  higherLatency: boolean;
  enabled: boolean;
  /** Illustrative value this type catches, shown as a hover hint (curated only). */
  example?: string;
}

function asStringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? (value.filter(v => typeof v === 'string') as string[])
    : [];
}

function labelFor(name: string, option: PiiInfoTypeOption | undefined): string {
  return getCuratedPiiInfoType(name)?.label ?? option?.displayName ?? name;
}

function isSensitive(
  name: string,
  option: PiiInfoTypeOption | undefined
): boolean {
  const curated = getCuratedPiiInfoType(name);
  if (curated) return curated.sensitivity === 'sensitive';
  if (option) return option.sensitivity === 'sensitive';
  return isSensitivePiiInfoType(name);
}

function isHigherLatency(
  name: string,
  option: PiiInfoTypeOption | undefined
): boolean {
  return (
    getCuratedPiiInfoType(name)?.higherLatency ?? option?.higherLatency ?? false
  );
}

/**
 * Bespoke "Detected PII Types" field (KIR-3837). Renders the curated PII types
 * (plus any admin-added DLP types) as a table with per-type On/Off toggles,
 * sensitivity badges and a "Higher latency" tag, and a searchable picker sourced
 * from the full Google DLP infoType list. Adding a higher-latency type prompts a
 * confirmation and is added turned OFF; normal types are added ON.
 *
 * Backed by three form fields: `infoTypes` (enabled + scanned),
 * `selectedInfoTypes` (every added row, incl. off ones, so an added off row
 * persists across saves without being scanned) and `removedInfoTypes` (curated
 * rows the admin deleted — KIR-4902: every row has the same toggle + delete).
 */
export function PiiTypesField({
  control,
  readOnly = false,
}: PiiTypesFieldProps): ReactElement {
  const { field: enabledField } = useController({
    name: 'infoTypes',
    control,
  });
  const { field: selectedField } = useController({
    name: 'selectedInfoTypes',
    control,
  });
  const { field: removedField } = useController({
    name: 'removedInfoTypes',
    control,
  });

  const [search, setSearch] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pendingLatencyType, setPendingLatencyType] =
    useState<PiiInfoTypeOption | null>(null);

  const enabled = useMemo(
    () => asStringArray(enabledField.value),
    [enabledField.value]
  );
  const selected = useMemo(
    () => asStringArray(selectedField.value),
    [selectedField.value]
  );
  const removed = useMemo(
    () => asStringArray(removedField.value),
    [removedField.value]
  );

  // Only fetch the DLP catalog when the picker is opened or when non-curated
  // rows exist (to annotate them) — the policy card doesn't preload it otherwise
  // (KIR-3837: "do not load all 150+ types at once").
  const hasExtraRows = useMemo(
    () => [...selected, ...enabled].some(name => !getCuratedPiiInfoType(name)),
    [selected, enabled]
  );
  const { options, isLoading, isError } = usePiiInfoTypes(
    pickerOpen || hasExtraRows
  );

  const optionByName = useMemo(() => {
    const map = new Map<string, PiiInfoTypeOption>();
    for (const o of options) map.set(o.name, o);
    return map;
  }, [options]);

  // Rows: curated types (in catalog order, minus ones the admin removed)
  // followed by any added or enabled type not in the curated catalog. A removed
  // curated type that is still enabled (e.g. set via the API) stays visible so
  // nothing is scanned without a row.
  const rows = useMemo<PiiRow[]>(() => {
    const enabledSet = new Set(enabled);
    const removedSet = new Set(removed);
    const seen = new Set<string>();
    const out: PiiRow[] = [];

    for (const t of CURATED_PII_INFO_TYPES) {
      seen.add(t.dlpName);
      if (removedSet.has(t.dlpName) && !enabledSet.has(t.dlpName)) continue;
      out.push({
        name: t.dlpName,
        label: t.label,
        sensitive: t.sensitivity === 'sensitive',
        higherLatency: t.higherLatency,
        enabled: enabledSet.has(t.dlpName),
        example: t.example,
      });
    }
    for (const name of [...selected, ...enabled]) {
      if (seen.has(name)) continue;
      seen.add(name);
      const opt = optionByName.get(name);
      out.push({
        name,
        label: labelFor(name, opt),
        sensitive: isSensitive(name, opt),
        higherLatency: isHigherLatency(name, opt),
        enabled: enabledSet.has(name),
        example: opt?.example,
      });
    }
    return out;
  }, [enabled, selected, removed, optionByName]);

  const rowNames = useMemo(() => new Set(rows.map(r => r.name)), [rows]);

  // The popover's anchor, so a pointer-down on / focus into the search box is
  // not treated as an outside-dismiss — see `onInteractOutside` below.
  const anchorRef = useRef<HTMLDivElement>(null);

  // Every match, in a scrollable list (KIR-4818) — a cap hid most of the
  // catalog behind "keep typing", so an admin could not browse what exists.
  const pickerResults = useMemo(() => {
    const q = search.trim().toUpperCase();
    if (!q) return options;
    // Match the label the entry actually shows (the curated label for curated
    // types) as well as the id and DLP's own displayName.
    return options.filter(
      o =>
        o.name.toUpperCase().includes(q) ||
        (o.displayName ?? '').toUpperCase().includes(q) ||
        labelFor(o.name, o).toUpperCase().includes(q)
    );
  }, [options, search]);

  // KIR-4818: entries show the same human-readable label as the table row. Two
  // DLP types can share a display name, so only those entries also show the
  // infoType id to tell them apart. Counted over the whole catalog (not the
  // filtered results) and case-insensitively, so an entry's text stays stable
  // while the admin types.
  const duplicateLabels = useMemo(() => {
    const counts = new Map<string, number>();
    for (const o of options) {
      const key = labelFor(o.name, o).toLowerCase();
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return new Set([...counts].filter(([, n]) => n > 1).map(([k]) => k));
  }, [options]);

  const setEnabled = (names: string[]): void => {
    enabledField.onChange(names);
  };
  const setSelected = (names: string[]): void => {
    selectedField.onChange(names);
  };
  const setRemoved = (names: string[]): void => {
    removedField.onChange(names);
  };

  // Show a type as a row again: a removed curated type leaves the removed list
  // (curated rows render by default); any other type joins `selectedInfoTypes`.
  const showRow = (name: string): void => {
    if (getCuratedPiiInfoType(name)) {
      if (removed.includes(name)) setRemoved(removed.filter(n => n !== name));
      return;
    }
    if (!selected.includes(name)) setSelected([...selected, name]);
  };

  const toggle = (name: string, on: boolean): void => {
    if (readOnly) return;
    if (on) {
      if (!enabled.includes(name)) setEnabled([...enabled, name]);
      if (!selected.includes(name) && !getCuratedPiiInfoType(name)) {
        setSelected([...selected, name]);
      }
    } else {
      setEnabled(enabled.filter(n => n !== name));
      // Keep a non-curated type visible as an Off row after it is turned off —
      // curated rows always render, but an added / migrated picker type would
      // otherwise disappear from the table (KIR-3837 Bugbot).
      if (!selected.includes(name) && !getCuratedPiiInfoType(name)) {
        setSelected([...selected, name]);
      }
    }
  };

  const addType = (option: PiiInfoTypeOption): void => {
    if (readOnly) return;
    if (rowNames.has(option.name)) return;
    if (option.higherLatency) {
      setPendingLatencyType(option); // confirm; added OFF
      return;
    }
    // Normal type: added ON.
    setEnabled(
      enabled.includes(option.name) ? enabled : [...enabled, option.name]
    );
    showRow(option.name);
    setSearch('');
    setPickerOpen(false);
  };

  const confirmAddLatency = (): void => {
    const opt = pendingLatencyType;
    setPendingLatencyType(null);
    if (!opt) return;
    // Added OFF: shown as a row but not scanned (not enabled).
    showRow(opt.name);
    setSearch('');
    setPickerOpen(false);
  };

  // KIR-4818: name the type readably in the confirmation, keeping the id when
  // it differs from the label.
  const pendingLatencyLabel = ((): string => {
    if (!pendingLatencyType) return 'This type';
    const label = labelFor(pendingLatencyType.name, pendingLatencyType);
    return label === pendingLatencyType.name
      ? label
      : `${label} (${pendingLatencyType.name})`;
  })();

  const removeRow = (name: string): void => {
    if (readOnly) return;
    setEnabled(enabled.filter(n => n !== name));
    setSelected(selected.filter(n => n !== name));
    // KIR-4902: curated rows always render, so record the removal explicitly.
    if (getCuratedPiiInfoType(name) && !removed.includes(name)) {
      setRemoved([...removed, name]);
    }
  };

  return (
    <div className="space-y-3">
      <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
        <PopoverAnchor asChild>
          <div className="relative" ref={anchorRef}>
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={e => {
                setSearch(e.target.value);
                if (!pickerOpen) setPickerOpen(true);
              }}
              onFocus={() => setPickerOpen(true)}
              placeholder="Search and add PII types..."
              className="pl-9"
              disabled={readOnly}
              aria-label="Search and add PII types"
            />
          </div>
        </PopoverAnchor>
        <PopoverContent
          className="w-[--radix-popover-trigger-width] p-0"
          align="start"
          onOpenAutoFocus={e => e.preventDefault()}
          // Keep the popover open when the pointer or focus lands on the
          // search box itself. The Input lives in a PopoverAnchor, not a
          // PopoverTrigger, so Radix counts it as "outside" the content:
          //  - KIR-4839: its pointer-down dismissed the popover.
          //  - KIR-4818: its focus did too. `onFocus` opens the popover while
          //    the input's `focusin` is still bubbling; Radix attaches its
          //    document focus-outside listener in that same synchronous
          //    commit, so the very event that opened it dismissed it ~200ms
          //    later — the "blink" on click.
          // `onInteractOutside` covers both. Without it an admin has to guess
          // a DLP infoType id before seeing any suggestion (KIR-3837).
          onInteractOutside={e => {
            if (anchorRef.current?.contains(e.target as Node)) {
              e.preventDefault();
            }
          }}
        >
          <div className="max-h-72 overflow-y-auto">
            <div className="px-3 py-2 text-xs text-muted-foreground">
              Types come from Google DLP. Scroll, or type to search.
            </div>
            {pickerResults.map(o => {
              const already = rowNames.has(o.name);
              const label = labelFor(o.name, o);
              return (
                <button
                  key={o.name}
                  type="button"
                  // KIR-4532: name-keyed hook for E2E. The entry's text is the
                  // display name, which differs between the live DLP catalog
                  // and the offline fake engine, so address entries by id.
                  data-pii-option={o.name}
                  title={o.name}
                  disabled={already || readOnly}
                  onClick={() => addType(o)}
                  className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-muted disabled:opacity-60"
                >
                  <span className="flex items-center gap-2 truncate">
                    <span className="truncate font-medium">{label}</span>
                    {duplicateLabels.has(label.toLowerCase()) && (
                      <span className="truncate font-mono text-xs text-muted-foreground">
                        {o.name}
                      </span>
                    )}
                    <SensitivityBadge
                      sensitive={o.sensitivity === 'sensitive'}
                    />
                    {o.higherLatency && <HigherLatencyTag />}
                  </span>
                  {already ? (
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Check className="h-3 w-3" /> Added
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-xs text-primary">
                      <Plus className="h-3 w-3" /> Add
                    </span>
                  )}
                </button>
              );
            })}
            {pickerResults.length === 0 &&
              (isLoading ? (
                <div className="px-3 py-2 text-sm text-muted-foreground">
                  Loading types…
                </div>
              ) : isError ? (
                <div className="px-3 py-2 text-sm text-destructive">
                  Couldn't load the DLP catalog. Try again shortly.
                </div>
              ) : (
                <div className="px-3 py-2 text-sm text-muted-foreground">
                  No matching types.
                </div>
              ))}
          </div>
        </PopoverContent>
      </Popover>

      <div className="rounded-md border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-xs uppercase text-muted-foreground">
              <th className="px-3 py-2 text-left font-medium">PII Type</th>
              <th className="px-3 py-2 text-left font-medium">Sensitivity</th>
              <th className="px-3 py-2 text-right font-medium">Detection</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr
                key={row.name}
                data-pii-type={row.name}
                className="border-b last:border-0"
              >
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{row.label}</span>
                    {row.example && (
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            aria-label={`Example for ${row.label}`}
                            className="text-muted-foreground hover:text-foreground"
                          >
                            <Info className="h-3.5 w-3.5" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent
                          align="start"
                          className="w-auto max-w-xs p-2 text-xs"
                        >
                          <span className="text-muted-foreground">
                            Catches e.g.{' '}
                          </span>
                          <code className="font-mono">{row.example}</code>
                        </PopoverContent>
                      </Popover>
                    )}
                    {row.higherLatency && <HigherLatencyTag />}
                  </div>
                </td>
                <td className="px-3 py-2">
                  <SensitivityBadge sensitive={row.sensitive} />
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center justify-end gap-2">
                    <span className="text-xs text-muted-foreground">
                      {row.enabled ? 'On' : 'Off'}
                    </span>
                    <Switch
                      checked={row.enabled}
                      onCheckedChange={on => toggle(row.name, on)}
                      disabled={readOnly}
                      aria-label={`Detect ${row.label}`}
                    />
                    <button
                      type="button"
                      onClick={() => removeRow(row.name)}
                      disabled={readOnly}
                      className="text-muted-foreground hover:text-destructive"
                      aria-label={`Remove ${row.label}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">
        Types that are Off stay in the list but are not scanned until you turn
        them on. Delete removes a type from the list — add it back from the
        search box, or use Reset to Defaults to restore the built-in set.
      </p>

      <ConfirmationDialog
        isOpen={pendingLatencyType !== null}
        onClose={() => setPendingLatencyType(null)}
        onConfirm={confirmAddLatency}
        variant="default"
        confirmLabel="Add as Off"
        title="Add a higher latency type?"
        description={`${pendingLatencyLabel} is a higher latency detector. Google marks it as slow, and because Kirin scans inline before content reaches the model, it adds delay to every scan. Kirin will add it turned Off — turn it on in the list when you are ready to accept the extra delay.`}
      />
    </div>
  );
}

function SensitivityBadge({ sensitive }: { sensitive: boolean }): ReactElement {
  return (
    <Badge
      variant="outline"
      className={
        sensitive
          ? 'border-red-200 text-red-600 dark:border-red-900 dark:text-red-400'
          : 'text-muted-foreground'
      }
    >
      {sensitive ? 'Sensitive' : 'Not sensitive'}
    </Badge>
  );
}

function HigherLatencyTag(): ReactElement {
  return (
    <span className="inline-flex items-center gap-1 rounded border border-amber-300 px-1.5 py-0.5 text-xs text-amber-700 dark:border-amber-800 dark:text-amber-400">
      <Clock className="h-3 w-3" /> Higher latency
    </span>
  );
}
